import { useCallback, useEffect, useState, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import Hidden from '../../HiddenSSRCompatible';
import { Box, Typography, Tooltip } from '@material-ui/core';
import { Chip, makeStyles } from '@material-ui/core';
import { usePermissions } from '../../../utils/PermissionProvider';
import { StoreContext } from '../../../store';
import PremiumNoticeModal from './premiumNoticeModal';
import { BAILEY_PERMISSION, BASIC_WHATSAPP_PERMISSION } from '../../../constants/permission';

const useStyles = makeStyles((theme) => ({
  accountSwitchWrap: {
    padding: theme.spacing(1, 1.5),
    borderBottom: '1px solid #e0e0e0',
    minWidth: 0,
  },
  accountToggler: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f5f6f6',
    borderRadius: 4,
    padding: theme.spacing(0.5),
    border: '1px solid #e0e0e0',
    minHeight: 36,
    gap: theme.spacing(1),
  },
  toggleChip: {
    flex: 1,
    height: 28,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 3,
    padding: '0 14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    marginLeft: 2,
    marginRight: 2,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
    '&:disabled': {
      cursor: 'default',
      pointerEvents: 'none',
    }
  },
  toggleChipActive: {
    backgroundColor: '#1976d2',
    color: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: '#1565c0',
    }
  },
  toggleChipInactive: {
    backgroundColor: 'transparent',
    color: '#666',
  },
}));

export default observer(function WhatsappAccountSwitcher() {
  const classes = useStyles();
  const { hasPermission } = usePermissions();
  const store = useContext(StoreContext);

  const [usingSuperkos, setUsingSuperkos] = useState(() => {
    try {
      return Boolean(store?.whatsapp?.isSuperkos);
    } catch (err) {
      return false;
    }
  });

  const [premiumModalOpen, setPremiumModalOpen] = useState(false);

  useEffect(() => {
    try {
      const val = Boolean(store?.whatsapp?.isSuperkos);
      setUsingSuperkos(val);
    } catch (err) {
      // ignore
    }
  }, [store?.whatsapp?.isSuperkos]);

  const handleAccountSwitch = useCallback((accountType) => {
    const toProperty = accountType === 'property';
    const toSuperkos = accountType === 'superkos';
    const canToggleFull = hasPermission && hasPermission(BAILEY_PERMISSION);
    const hasBasicWhatsapp = hasPermission && hasPermission(BASIC_WHATSAPP_PERMISSION);

    if (hasBasicWhatsapp && !canToggleFull && toProperty) {
      setPremiumModalOpen(true);
      return;
    }

    setUsingSuperkos(toSuperkos);
    try {
      if (store && store.whatsapp && typeof store.whatsapp.setIsSuperkos === 'function') {
        store.whatsapp.setIsSuperkos(toSuperkos);
      } else if (store && store.whatsapp) {
        store.whatsapp.isSuperkos = Boolean(toSuperkos);
      }
    } catch (err) {
      // ignore
    }
  }, [store, hasPermission]);

  if (!hasPermission || (!hasPermission(BASIC_WHATSAPP_PERMISSION) && !hasPermission(BAILEY_PERMISSION))) return null;

  return (
    <>
      <Tooltip
        title={"Pilih akun yang digunakan untuk mengirim pesan WhatsApp. Property untuk mengirim dari nomor properti; Superkos menampilkan percakapan lewat akun Superkos."}
        arrow
        placement="top"
      >
        <Box className={classes.accountSwitchWrap} aria-label="WhatsApp account switcher">
          <Hidden smDown>
            <Typography
              variant="caption"
              style={{
                marginBottom: 4,
                color: '#333',
                textAlign: 'center',
                width: '100%',
                display: 'block',
              }}
            >
              Pengiriman melalui:
            </Typography>
            <Box className={classes.accountToggler}>
              <Chip
                label="Property"
                size="small"
                clickable={usingSuperkos}
                onClick={() => {
                  handleAccountSwitch('property');
                }}
                className={`${classes.toggleChip} ${!usingSuperkos ? classes.toggleChipActive : classes.toggleChipInactive}`}
                style={{ backgroundColor: !usingSuperkos ? '#1976d2' : 'transparent', color: !usingSuperkos ? '#fff' : '#666' }}
              />

              <Chip
                label="Superkos"
                size="small"
                clickable={!usingSuperkos}
                onClick={() => {
                  handleAccountSwitch('superkos');
                }}
                className={`${classes.toggleChip} ${usingSuperkos ? classes.toggleChipActive : classes.toggleChipInactive}`}
                style={{ backgroundColor: usingSuperkos ? '#ff9800' : 'transparent', color: usingSuperkos ? '#fff' : '#666' }}
              />
            </Box>
          </Hidden>
        </Box>
      </Tooltip>

      <PremiumNoticeModal open={premiumModalOpen} onClose={() => setPremiumModalOpen(false)} onUpgrade={() => setPremiumModalOpen(false)} />
    </>
  );
});
